import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  AdminSupportingText,
  AdminHeaderAction,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  adminFormTwoColGridClass,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  CCT_MODULE_OPTIONS,
  COMPARISON_DATASET_OPTIONS,
  CONTENT_CONTROL_ROUTE,
  DETECTION_SOURCE_OPTIONS,
  LOOKBACK_UNIT_OPTIONS,
  NORMALISATION_CONTROL_OPTIONS,
  PROTECTED_TERM_OPTIONS,
  CHECKING_FAILURE_MESSAGE,
  buildSampleContentControlRules,
  createEmptyContentControlForm,
  formatConditionsPreview,
  formatContentControlDateTime,
  formatDetectionMethodList,
  formatLookbackPeriod,
  formatTriggerList,
  getApprovedPatternLabel,
  getApprovedPatternsForIssue,
  getAvailableDetectionMethods,
  getComparisonDatasetLabel,
  getContentAreaLabel,
  getContentAreasForModule,
  getContentContextLabel,
  getContentContextsForArea,
  getContentIssueLabel,
  getContentIssuesForArea,
  getDetectionMethodLabel,
  getDetectionServiceLabel,
  getDetectionServiceOptions,
  getDetectionSourceLabel,
  getMethodSource,
  getMethodSourceLabel,
  getModuleLabel,
  getNormalisationLabel,
  getProtectedTermLabel,
  getSpamContextLabel,
  getSpamContextsForIssue,
  getTriggersForScope,
  nextContentControlRuleId,
  normalizeContentControlForm,
  operatorIsMulti,
} from '../../../data/contentControlConfig';
import {
  canAddContentControlRule,
  canEditContentControlRule,
  canSaveContentControlRule,
  canViewContentControl,
  contentControlScopeKey,
  getAvailableConditionFields,
  getConditionValueOptions,
  getEditRuleSource,
  getPublishedRule,
  saveContentControlRule,
} from '../../../data/contentControlRuleWorkflow';
import { matchesUrlName } from '../../../utils/slugify';

function emptyForm() {
  return {
    ...createEmptyContentControlForm(),
    version: 1,
    pendingDraft: null,
    configurationHistory: [],
    testHistory: [],
  };
}

function mergeIncomingRule(raw, ruleIdParam) {
  const normalized = normalizeContentControlForm(raw || {});
  return {
    ...emptyForm(),
    ...normalized,
    id: normalized.id ?? ruleIdParam ?? '',
    version: raw?.version ?? 1,
    pendingDraft: raw?.pendingDraft ?? null,
    configurationHistory: raw?.configurationHistory ?? [],
    testHistory: raw?.testHistory ?? [],
  };
}

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

function clearMethodSettings(next, methods) {
  if (!methods.includes('exact_text_match')) next.exactTextValues = [];
  if (!methods.includes('word_or_phrase_match')) next.wordOrPhraseValues = [];
  if (!methods.includes('pattern_and_context_detection')) next.approvedPatterns = [];
  if (!methods.includes('protected_identity_detection')) next.protectedTerms = [];
  if (!methods.some((method) =>
    ['exact_text_match', 'word_or_phrase_match', 'pattern_and_context_detection', 'protected_identity_detection'].includes(method)
  )) {
    next.normalisationControls = [];
  }
  if (!methods.includes('similarity_and_spam_repetition_detection')) {
    next.comparisonDataset = '';
    next.similarityThreshold = '';
    next.repetitionCount = '';
    next.lookbackPeriodValue = '';
    next.lookbackPeriodUnit = '';
    next.spamContext = '';
  }
}

const MultiChecklist = ({ label, options, values, onChange, disabled, error, required = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>
      {label} {required ? <span className="text-alertred">*</span> : null}
    </AdminDetailFieldLabel>
    <div className="space-y-2">
      {disabled || !options.length ? (
        <AdminDetailFieldValue className="py-2">—</AdminDetailFieldValue>
      ) : (
        options.map((option) => (
          <label key={option.value} className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 mt-0.5 text-primaryblue shrink-0"
              checked={(values || []).includes(option.value)}
              onChange={(e) => {
                const next = new Set(values || []);
                if (e.target.checked) next.add(option.value);
                else next.delete(option.value);
                onChange([...next]);
              }}
            />
            <span className="text-offblack text-sm break-words">{option.label}</span>
          </label>
        ))
      )}
    </div>
    {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
  </div>
);

const ContentControlDetail = () => {
  const navigate = useNavigate();
  const { ruleName } = useParams();
  const location = useLocation();
  const { auth } = useAuth();
  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isCreate && !isEdit;
  const canView = canViewContentControl(auth);
  const canAdd = canAddContentControlRule(auth);
  const canEdit = canEditContentControlRule(auth);
  const canSave = canSaveContentControlRule(auth);

  const ruleFromState = location.state?.rule;
  const existingRulesFromState = location.state?.existingRules;

  const allRules = useMemo(
    () => existingRulesFromState ?? buildSampleContentControlRules(),
    [existingRulesFromState],
  );

  const publishedBaseline = useMemo(() => {
    if (isCreate) return null;
    if (ruleFromState?.id) {
      const fromList = allRules.find((r) => r.id === ruleFromState.id);
      return getPublishedRule(fromList || ruleFromState);
    }
    if (ruleName) {
      const found = allRules.find((r) => matchesUrlName(r.ruleName, ruleName));
      return getPublishedRule(found || null);
    }
    return null;
  }, [isCreate, ruleFromState, ruleName, allRules]);

  const resolvedSource = useMemo(() => {
    if (ruleFromState && isView) {
      const base = allRules.find((r) => r.id === ruleFromState.id) || ruleFromState;
      return mergeIncomingRule(getPublishedRule(base) || base, base.id);
    }
    if (ruleFromState) {
      const base = allRules.find((r) => r.id === ruleFromState.id) || ruleFromState;
      const source = isEdit ? getEditRuleSource(base) : getPublishedRule(base) || base;
      return mergeIncomingRule(source, base.id);
    }
    if (!isCreate && ruleName) {
      const found = allRules.find((r) => matchesUrlName(r.ruleName, ruleName));
      if (found) {
        const source = isEdit ? getEditRuleSource(found) : getPublishedRule(found);
        return mergeIncomingRule(source, found.id);
      }
    }
    return emptyForm();
  }, [ruleFromState, isCreate, isEdit, isView, ruleName, allRules]);

  const [formData, setFormData] = useState(resolvedSource);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData(resolvedSource);
  }, [resolvedSource]);

  const scopeLocked = Boolean(!isCreate && formData.id);
  const areaOptions = useMemo(() => getContentAreasForModule(formData.module), [formData.module]);
  const contextOptions = useMemo(
    () => getContentContextsForArea(formData.module, formData.contentArea),
    [formData.module, formData.contentArea],
  );
  const triggerOptions = useMemo(
    () => getTriggersForScope(formData.module, formData.contentArea, formData.contentContext),
    [formData.module, formData.contentArea, formData.contentContext],
  );
  const issueOptions = useMemo(() => getContentIssuesForArea(formData.contentArea), [formData.contentArea]);
  const methodValues = useMemo(() => getAvailableDetectionMethods(formData), [formData]);
  const methodOptions = useMemo(
    () =>
      methodValues.map((value) => {
        const source = formData.detectionSource === 'combined'
          ? getMethodSourceLabel(getMethodSource(value, formData.detectionSource, formData.detectionService))
          : '';
        return {
          value,
          label: source ? `${getDetectionMethodLabel(value)} (${source})` : getDetectionMethodLabel(value),
        };
      }),
    [methodValues, formData.detectionSource, formData.detectionService],
  );
  const serviceOptions = useMemo(() => getDetectionServiceOptions(formData), [formData]);
  const patternOptions = useMemo(() => getApprovedPatternsForIssue(formData.contentIssue), [formData.contentIssue]);
  const spamContextOptions = useMemo(() => getSpamContextsForIssue(formData.contentIssue), [formData.contentIssue]);
  const conditionFields = useMemo(() => getAvailableConditionFields(formData), [formData]);

  const methods = formData.detectionMethods || [];
  const showService = formData.detectionSource === 'approved_integration' || formData.detectionSource === 'combined';
  const showExact = methods.includes('exact_text_match');
  const showPhrase = methods.includes('word_or_phrase_match');
  const showPatterns = methods.includes('pattern_and_context_detection');
  const showTerms = methods.includes('protected_identity_detection');
  const showNorm = methods.some((method) =>
    ['exact_text_match', 'word_or_phrase_match', 'pattern_and_context_detection', 'protected_identity_detection'].includes(method),
  );
  const showSimilarity = methods.includes('similarity_and_spam_repetition_detection');

  const handleChange = (field, value) => {
    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
      activation: undefined,
      duplicate: undefined,
      overlap: undefined,
      conflict: undefined,
      permission: undefined,
      checksUnavailable: undefined,
    }));
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'module') {
        next.contentArea = '';
        next.contentContext = '';
        next.triggers = [];
        next.contentIssue = '';
        next.detectionMethods = [];
        next.conditions = [];
        clearMethodSettings(next, []);
      }
      if (field === 'contentArea') {
        next.contentContext = '';
        next.triggers = [];
        next.contentIssue = '';
        next.detectionMethods = [];
        next.conditions = [];
        clearMethodSettings(next, []);
      }
      if (field === 'contentContext') {
        next.triggers = [];
      }
      if (field === 'contentIssue') {
        next.detectionMethods = [];
        next.approvedPatterns = [];
        next.spamContext = '';
        next.conditions = [];
        clearMethodSettings(next, []);
      }
      if (field === 'detectionSource') {
        if (value === 'internal') next.detectionService = '';
        next.detectionMethods = [];
        clearMethodSettings(next, []);
      }
      if (field === 'detectionService') {
        next.detectionMethods = [];
        clearMethodSettings(next, []);
      }
      if (field === 'detectionMethods') {
        clearMethodSettings(next, value);
        const allowedFields = getAvailableConditionFields({ ...next, detectionMethods: value }).map((option) => option.value);
        next.conditions = (next.conditions || []).filter((condition) => !condition.field || allowedFields.includes(condition.field));
      }
      // Live responses are always Block / Continue. Flags are owned by Configuration — Flag.
      next.confirmedAction = 'block_current_action';
      next.confirmedReviewRequirement = 'no_review_item_required';
      next.suspectedAction = 'block_current_action';
      next.suspectedReviewRequirement = 'no_review_item_required';
      next.holdMessage = '';
      next.warningTitle = '';
      next.warningMessage = '';
      next.warningResponse = '';
      return next;
    });
  };

  const buildRecord = (nowIso, userLabel) => {
    const id = formData.id || nextContentControlRuleId(allRules);
    const isNew = !formData.id;
    return {
      ...formData,
      id,
      ruleName: formData.ruleName.trim(),
      description: formData.description.trim(),
      changeReason: formData.changeReason.trim(),
      version: formData.version || publishedBaseline?.version || 1,
      pendingDraft: formData.pendingDraft ?? null,
      configurationHistory: formData.configurationHistory || [],
      testHistory: formData.testHistory || [],
      dateAdded: isNew ? nowIso : formData.dateAdded || nowIso,
      addedBy: isNew ? userLabel : formData.addedBy || userLabel,
      lastUpdated: nowIso,
      lastUpdatedBy: userLabel,
    };
  };

  const handleSave = () => {
    const nowIso = new Date().toISOString();
    const userLabel = 'Olivia Brown';
    setErrors((prev) => ({ ...prev, activation: undefined }));

    if ((isCreate && !canAdd) || (isEdit && !canEdit) || !canSave) {
      setErrors({
        permission: 'You do not have permission to save this Rule.',
        activation: 'You do not have permission to save this Rule.',
      });
      return;
    }

    const record = buildRecord(nowIso, userLabel);
    let result;
    try {
      result = saveContentControlRule(allRules, record, publishedBaseline || {}, {
        originalLastUpdated: resolvedSource.lastUpdated,
        originalScopeKey: publishedBaseline ? contentControlScopeKey(publishedBaseline) : undefined,
        canSave: true,
        checksAvailable: true,
      });
    } catch {
      setErrors({
        checksUnavailable: 'The Rule could not be checked. Try again.',
        activation: 'The Rule could not be checked. Try again.',
      });
      return;
    }

    if (!result.success) {
      const next = { ...result.errors };
      if (result.summary.length) next.activation = result.summary.join(' ');
      setErrors(next);
      return;
    }

    navigate(CONTENT_CONTROL_ROUTE, { state: { rules: result.rules } });
  };

  const renderSelect = (name, label, options, placeholder, disabledExtra = false, required = true) => {
    if (isView) {
      const selected = options.find((o) => o.value === formData[name]);
      return <TextValue label={label} value={selected?.label || formData[name] || '—'} />;
    }
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required && <span className="text-alertred">*</span>}
        </AdminDetailFieldLabel>
        <div className="relative w-full min-w-0">
          <select
            id={`cct-${name}-select`}
            value={formData[name] || ''}
            title={options.find((o) => o.value === formData[name])?.label || placeholder}
            onChange={(e) => handleChange(name, e.target.value)}
            disabled={disabledExtra}
            className={adminFormDetailSelectClasses(disabledExtra, Boolean(errors[name]))}
          >
            <option value="">{placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <AdminFormDropdownChevron />
        </div>
        {errors[name] && <AdminFormFieldError>{errors[name]}</AdminFormFieldError>}
      </div>
    );
  };

  const renderTextarea = (name, label, placeholder, required = false) => {
    if (isView) return <TextValue label={label} value={formData[name]} />;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required ? <span className="text-alertred">*</span> : null}
        </AdminDetailFieldLabel>
        <textarea
          id={`cct-${name}`}
          placeholder={placeholder}
          value={formData[name] || ''}
          onChange={(e) => handleChange(name, e.target.value)}
          className={adminFormDetailTextareaClasses(false, Boolean(errors[name]))}
          rows={3}
        />
        {errors[name] ? <AdminFormFieldError>{errors[name]}</AdminFormFieldError> : null}
      </div>
    );
  };

  const renderLineList = (name, label, placeholder, required = false) => {
    const text = Array.isArray(formData[name]) ? formData[name].join('\n') : '';
    if (isView) return <TextValue label={label} value={(formData[name] || []).join('\n')} />;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required ? <span className="text-alertred">*</span> : null}
        </AdminDetailFieldLabel>
        <textarea
          placeholder={placeholder}
          value={text}
          onChange={(e) => handleChange(name, e.target.value.split('\n'))}
          className={adminFormDetailTextareaClasses(false, Boolean(errors[name]))}
          rows={4}
        />
        {errors[name] ? <AdminFormFieldError>{errors[name]}</AdminFormFieldError> : null}
      </div>
    );
  };

  const updateCondition = (index, patch) => {
    setErrors((prev) => ({ ...prev, [`condition_${index}`]: undefined, activation: undefined }));
    setFormData((prev) => {
      const conditions = [...(prev.conditions || [])];
      conditions[index] = { ...conditions[index], ...patch };
      if (patch.field) conditions[index].values = [];
      if (patch.operator && !operatorIsMulti(patch.operator)) {
        conditions[index].values = (conditions[index].values || []).slice(0, 1);
      }
      return { ...prev, conditions };
    });
  };

  const detectionFields = (
    <>
      {renderSelect('detectionSource', 'Detection Source', DETECTION_SOURCE_OPTIONS, 'Select detection source')}
      {showService
        ? renderSelect(
            'detectionService',
            'Detection Service',
            serviceOptions,
            'Select detection service',
            !formData.contentArea,
          )
        : null}
      <MultiChecklist
        label="Detection Methods"
        options={methodOptions}
        values={formData.detectionMethods}
        onChange={(value) => handleChange('detectionMethods', value)}
        disabled={!formData.contentIssue}
        error={errors.detectionMethods}
        required
      />
      {showExact ? renderLineList('exactTextValues', 'Exact Text Values', 'Enter one value per line', true) : null}
      {showPhrase ? renderLineList('wordOrPhraseValues', 'Word or Phrase Values', 'Enter one value per line', true) : null}
      {showPatterns ? (
        <MultiChecklist
          label="Approved Patterns"
          options={patternOptions}
          values={formData.approvedPatterns}
          onChange={(value) => handleChange('approvedPatterns', value)}
          error={errors.approvedPatterns}
          required
        />
      ) : null}
      {showTerms ? (
        <MultiChecklist
          label="Protected Terms"
          options={PROTECTED_TERM_OPTIONS}
          values={formData.protectedTerms}
          onChange={(value) => handleChange('protectedTerms', value)}
          error={errors.protectedTerms}
          required
        />
      ) : null}
      {showNorm ? (
        <MultiChecklist
          label="Normalisation Controls"
          options={NORMALISATION_CONTROL_OPTIONS}
          values={formData.normalisationControls}
          onChange={(value) => handleChange('normalisationControls', value)}
        />
      ) : null}
      {showSimilarity ? (
        <>
          {renderSelect('comparisonDataset', 'Comparison Dataset', COMPARISON_DATASET_OPTIONS, 'Select comparison dataset')}
          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Similarity Threshold <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <input
              type="text"
              placeholder="Enter 1 to 100"
              value={formData.similarityThreshold === '' || formData.similarityThreshold == null ? '' : String(formData.similarityThreshold)}
              onChange={(e) => handleChange('similarityThreshold', e.target.value.replace(/[^\d]/g, ''))}
              className={adminFormDetailInputClasses(false, Boolean(errors.similarityThreshold))}
            />
            {errors.similarityThreshold ? <AdminFormFieldError>{errors.similarityThreshold}</AdminFormFieldError> : null}
          </div>
          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Repetition Count <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <input
              type="text"
              placeholder="Enter 2 or more"
              value={formData.repetitionCount === '' || formData.repetitionCount == null ? '' : String(formData.repetitionCount)}
              onChange={(e) => handleChange('repetitionCount', e.target.value.replace(/[^\d]/g, ''))}
              className={adminFormDetailInputClasses(false, Boolean(errors.repetitionCount))}
            />
            {errors.repetitionCount ? <AdminFormFieldError>{errors.repetitionCount}</AdminFormFieldError> : null}
          </div>
          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Lookback Period <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <input
              type="text"
              placeholder="Enter a positive number"
              value={formData.lookbackPeriodValue === '' || formData.lookbackPeriodValue == null ? '' : String(formData.lookbackPeriodValue)}
              onChange={(e) => handleChange('lookbackPeriodValue', e.target.value.replace(/[^\d]/g, ''))}
              className={adminFormDetailInputClasses(false, Boolean(errors.lookbackPeriodValue))}
            />
            {errors.lookbackPeriodValue ? <AdminFormFieldError>{errors.lookbackPeriodValue}</AdminFormFieldError> : null}
          </div>
          {renderSelect('lookbackPeriodUnit', 'Lookback Period Unit', LOOKBACK_UNIT_OPTIONS, 'Select unit')}
          {renderSelect('spamContext', 'Spam Context', spamContextOptions, 'Select spam context')}
        </>
      ) : null}
    </>
  );

  const responseFields = (
    <>
      <AdminConfigLockedField label="Confirmed Match Action" value="Block Current Action" isView={false} />
      <AdminConfigLockedField label="Suspected Match Action" value="Block Current Action" isView={false} />
      <AdminConfigLockedField label="No Match Behaviour" value="Continue Current Action" isView={false} />
      <AdminConfigLockedField label="Checking-failure message" value={CHECKING_FAILURE_MESSAGE} isView={false} />
      {renderTextarea('blockMessage', 'Block Message', 'Enter block message', true)}
    </>
  );

  const conditionBlock = (
    <div className="space-y-3">
      <AdminSupportingText className="mb-0">
        {(formData.conditions || []).length
          ? `${(formData.conditions || []).length} added`
          : 'No Conditions added'}
      </AdminSupportingText>
      {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}
      {(formData.conditions || []).length ? (
        <div className="space-y-3">
          {(formData.conditions || []).map((condition, index) => {
            const valueOptions = getConditionValueOptions(formData, condition.field);
            const multi = operatorIsMulti(condition.operator);
            return (
              <div key={condition.id || index} className="rounded-xl border border-lightgray p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <AdminSupportingText className="mb-0 font-semibold !text-offblack">
                    Condition
                  </AdminSupportingText>
                  <button
                    type="button"
                    onClick={() => handleChange('conditions', (formData.conditions || []).filter((_, conditionIndex) => conditionIndex !== index))}
                    className="shrink-0 inline-flex items-center justify-center leading-none text-offblack hover:text-alertred"
                    aria-label="Remove Condition"
                  >
                    <i className="fi fi-rs-trash text-sm" aria-hidden />
                  </button>
                </div>
                {errors[`condition_${index}`] ? <AdminFormFieldError>{errors[`condition_${index}`]}</AdminFormFieldError> : null}
                <div className="space-y-1 w-full min-w-0">
                  <AdminDetailFieldLabel>Field <span className="text-alertred">*</span></AdminDetailFieldLabel>
                  <div className="relative w-full min-w-0">
                    <select
                      value={condition.field || ''}
                      onChange={(e) => updateCondition(index, { field: e.target.value })}
                      className={adminFormDetailSelectClasses(false, Boolean(errors[`condition_${index}`]))}
                    >
                      <option value="">Select field</option>
                      {conditionFields.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <AdminFormDropdownChevron />
                  </div>
                </div>
                <div className="space-y-1 w-full min-w-0">
                  <AdminDetailFieldLabel>Operator <span className="text-alertred">*</span></AdminDetailFieldLabel>
                  <div className="relative w-full min-w-0">
                    <select
                      value={condition.operator || ''}
                      onChange={(e) => updateCondition(index, { operator: e.target.value })}
                      disabled={!condition.field}
                      className={adminFormDetailSelectClasses(!condition.field)}
                    >
                      <option value="">Select operator</option>
                      <option value="equals">Equals</option>
                      <option value="does_not_equal">Does Not Equal</option>
                      <option value="is_any_of">Is Any Of</option>
                      <option value="is_not_any_of">Is Not Any Of</option>
                    </select>
                    <AdminFormDropdownChevron />
                  </div>
                </div>
                {multi ? (
                  <MultiChecklist
                    label="Value"
                    options={valueOptions}
                    values={condition.values}
                    onChange={(value) => updateCondition(index, { values: value })}
                    disabled={!condition.field}
                    required
                  />
                ) : (
                  <div className="space-y-1 w-full min-w-0">
                    <AdminDetailFieldLabel>Value <span className="text-alertred">*</span></AdminDetailFieldLabel>
                    <div className="relative w-full min-w-0">
                      <select
                        value={(condition.values || [])[0] || ''}
                        onChange={(e) => updateCondition(index, { values: e.target.value ? [e.target.value] : [] })}
                        disabled={!condition.field}
                        className={adminFormDetailSelectClasses(!condition.field)}
                      >
                        <option value="">Select value</option>
                        {valueOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <AdminFormDropdownChevron />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
      {conditionFields.length ? (
        <button
          type="button"
          onClick={() => handleChange('conditions', [
            ...(formData.conditions || []),
            { id: `cond-${Date.now()}`, field: '', operator: '', values: [] },
          ])}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primaryblue hover:text-brightblue"
        >
          <i className="fi fi-rs-plus text-xs" aria-hidden />
          Add Condition
        </button>
      ) : null}
    </div>
  );

  if (!canView || (isCreate && !canAdd) || (isEdit && !canEdit)) {
    return (
      <AdminLayout>
        <AdminSupportingText className={`${ADMIN_PAGE_TITLE_CLASS} text-mediumgray`}>
          You do not have permission to {isView ? 'view' : isCreate ? 'add' : 'edit'} this Rule.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  if (isView) {
    const conditionsPreview = formatConditionsPreview(formData.conditions);
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>
          Content Control Rule Detail
        </AdminPageTitle>

        <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className={`${adminFormTwoColGridClass} gap-y-4`}>
              <TextValue label="Rule ID" value={formData.id} mono />
              <TextValue label="Rule Name" value={formData.ruleName} />
              <div className="sm:col-span-2">
                <TextValue label="Description" value={formData.description} />
              </div>
              <TextValue label="Module" value={getModuleLabel(formData.module)} />
              <TextValue label="Content Area" value={getContentAreaLabel(formData.contentArea)} />
              <TextValue label="Content Context" value={getContentContextLabel(formData.contentContext)} />
              <TextValue label="Content Issue" value={getContentIssueLabel(formData.contentIssue)} />
              <div className="sm:col-span-2">
                <TextValue label="Triggers" value={formatTriggerList(formData.triggers)} />
              </div>
              <TextValue label="Detection Source" value={getDetectionSourceLabel(formData.detectionSource)} />
              {showService ? (
                <TextValue label="Detection Service" value={getDetectionServiceLabel(formData.detectionService)} />
              ) : null}
              <div className="sm:col-span-2">
                <TextValue label="Detection Methods" value={formatDetectionMethodList(methods)} />
              </div>
              {showExact ? (
                <div className="sm:col-span-2">
                  <TextValue label="Exact Text Values" value={(formData.exactTextValues || []).join('\n')} />
                </div>
              ) : null}
              {showPhrase ? (
                <div className="sm:col-span-2">
                  <TextValue label="Word or Phrase Values" value={(formData.wordOrPhraseValues || []).join('\n')} />
                </div>
              ) : null}
              {showPatterns ? (
                <div className="sm:col-span-2">
                  <TextValue
                    label="Approved Patterns"
                    value={(formData.approvedPatterns || []).map(getApprovedPatternLabel).join(' · ')}
                  />
                </div>
              ) : null}
              {showTerms ? (
                <div className="sm:col-span-2">
                  <TextValue
                    label="Protected Terms"
                    value={(formData.protectedTerms || []).map(getProtectedTermLabel).join(' · ')}
                  />
                </div>
              ) : null}
              {showNorm ? (
                <div className="sm:col-span-2">
                  <TextValue
                    label="Normalisation Controls"
                    value={(formData.normalisationControls || []).map(getNormalisationLabel).join(' · ')}
                  />
                </div>
              ) : null}
              {showSimilarity ? (
                <>
                  <TextValue label="Comparison Dataset" value={getComparisonDatasetLabel(formData.comparisonDataset)} />
                  <TextValue
                    label="Similarity Threshold"
                    value={formData.similarityThreshold ? `${formData.similarityThreshold}%` : '—'}
                  />
                  <TextValue label="Repetition Count" value={formData.repetitionCount || '—'} />
                  <TextValue
                    label="Lookback Period"
                    value={formatLookbackPeriod(formData.lookbackPeriodValue, formData.lookbackPeriodUnit)}
                  />
                  <TextValue label="Spam Context" value={getSpamContextLabel(formData.spamContext)} />
                </>
              ) : null}
              <div className="sm:col-span-2">
                <TextValue
                  label="Conditions"
                  value={conditionsPreview.lines.length ? conditionsPreview.lines.join('\n') : 'No Conditions'}
                />
              </div>
              <TextValue label="Confirmed Match Action" value="Block Current Action" />
              <TextValue label="Suspected Match Action" value="Block Current Action" />
              <TextValue label="No Match Behaviour" value="Continue Current Action" />
              <div className="sm:col-span-2">
                <TextValue label="Block Message" value={formData.blockMessage} />
              </div>
              <div className="sm:col-span-2">
                <TextValue label="Checking-failure message" value={CHECKING_FAILURE_MESSAGE} />
              </div>
              <TextValue label="Date Added" value={formatContentControlDateTime(formData.dateAdded)} />
              <TextValue label="Added By" value={formData.addedBy} />
              <TextValue label="Last Updated" value={formatContentControlDateTime(formData.lastUpdated)} />
              <TextValue label="Last Updated By" value={formData.lastUpdatedBy} />
            </div>
          </DashboardMainContainer>
        </div>
      </AdminLayout>
    );
  }

  const hasValidationErrors = Boolean(
    errors.activation
    || errors.duplicate
    || errors.overlap
    || errors.conflict
    || errors.concurrent
    || errors.permission
    || errors.checksUnavailable
    || errors.fixedScope,
  );

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={isCreate ? 'Add Content Control Rule' : 'Edit Content Control Rule'}
        onSubmit={handleSave}
        showSave={false}
      >
        {formData.id ? (
          <AdminConfigLockedField label="Rule ID" value={formData.id} mono isView={false} />
        ) : null}
        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>
            Rule Name <span className="text-alertred">*</span>
          </AdminDetailFieldLabel>
          <input
            id="cct-rule-name"
            type="text"
            placeholder="Enter rule name"
            value={formData.ruleName || ''}
            onChange={(e) => handleChange('ruleName', e.target.value)}
            className={adminFormDetailInputClasses(false, Boolean(errors.ruleName))}
          />
          {errors.ruleName && <AdminFormFieldError>{errors.ruleName}</AdminFormFieldError>}
        </div>
        {renderTextarea('description', 'Description', 'Enter description', true)}
        {scopeLocked ? (
          <>
            <AdminConfigLockedField label="Module" value={getModuleLabel(formData.module)} isView={false} />
            <AdminConfigLockedField label="Content Area" value={getContentAreaLabel(formData.contentArea)} isView={false} />
            <AdminConfigLockedField label="Content Context" value={getContentContextLabel(formData.contentContext)} isView={false} />
            <AdminConfigLockedField label="Content Issue" value={getContentIssueLabel(formData.contentIssue)} isView={false} />
          </>
        ) : (
          <>
            {renderSelect('module', 'Module', CCT_MODULE_OPTIONS, 'Select module')}
            {renderSelect(
              'contentArea',
              'Content Area',
              areaOptions,
              formData.module ? 'Select content area' : 'Select module first',
              !formData.module,
            )}
            {renderSelect(
              'contentContext',
              'Content Context',
              contextOptions,
              formData.contentArea ? 'Select content context' : 'Select content area first',
              !formData.contentArea,
            )}
            {renderSelect(
              'contentIssue',
              'Content Issue',
              issueOptions,
              formData.contentArea ? 'Select content issue' : 'Select content area first',
              !formData.contentArea,
            )}
          </>
        )}
        <MultiChecklist
          label="Triggers"
          options={triggerOptions}
          values={formData.triggers}
          onChange={(value) => handleChange('triggers', value)}
          disabled={!formData.contentContext}
          error={errors.triggers}
          required
        />
        {detectionFields}
        {responseFields}
        {conditionBlock}
        {renderTextarea('changeReason', 'Change Reason', 'Enter a change reason', true)}
        {hasValidationErrors ? (
          <div className="space-y-2">
            {errors.permission ? <AdminFormFieldError>{errors.permission}</AdminFormFieldError> : null}
            {errors.checksUnavailable ? <AdminFormFieldError>{errors.checksUnavailable}</AdminFormFieldError> : null}
            {errors.duplicate ? <AdminFormFieldError>{errors.duplicate}</AdminFormFieldError> : null}
            {errors.conflict ? <AdminFormFieldError>{errors.conflict}</AdminFormFieldError> : null}
            {errors.overlap ? <AdminFormFieldError>{errors.overlap}</AdminFormFieldError> : null}
            {errors.concurrent ? <AdminFormFieldError>{errors.concurrent}</AdminFormFieldError> : null}
            {errors.fixedScope ? <AdminFormFieldError>{errors.fixedScope}</AdminFormFieldError> : null}
            {errors.activation ? <AdminFormFieldError>{errors.activation}</AdminFormFieldError> : null}
          </div>
        ) : null}
        <div className="flex items-center justify-end">
          <AdminHeaderAction type="submit" disabled={!canSave}>Save</AdminHeaderAction>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default ContentControlDetail;
